import { forwardRef } from 'react';
import { cn } from '../../lib/utils';
import { Loader2 } from 'lucide-react';

export interface ChatMessageProps {
  id: number;
  content: string;
  isAssistant: boolean;
  timestamp: Date;
  isLoading?: boolean;
  className?: string;
}

const ChatMessage = forwardRef<HTMLDivElement, ChatMessageProps>(
  ({ id, content, isAssistant, timestamp, isLoading = false, className }, ref) => {
    // Format WhatsApp style text with basic markdown
    const formatWhatsAppText = (text: string) => {
      if (!text) return '';

      // Replace *bold* with <strong>bold</strong>
      let formattedText = text.replace(/\*(.*?)\*/g, '<strong>$1</strong>');

      // Replace _italic_ with <em>italic</em>
      formattedText = formattedText.replace(/_(.*?)_/g, '<em>$1</em>');

      // Replace ~strikethrough~ with <del>strikethrough</del>
      formattedText = formattedText.replace(/~(.*?)~/g, '<del>$1</del>');

      // Replace ```code``` with <code>code</code>
      formattedText = formattedText.replace(/```(.*?)```/g, '<code>$1</code>');

      // Replace URLs with clickable links
      formattedText = formattedText.replace(
        /(https?:\/\/[^\s]+)/g,
        '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-blue-600 underline">$1</a>'
      );

      // Replace line breaks with <br>
      formattedText = formattedText.replace(/\n/g, '<br>');

      return formattedText;
    };

    return (
      <div
        ref={ref}
        className={cn('flex w-full', isAssistant ? 'justify-start' : 'justify-end', className)}
      >
        <div
          className={cn(
            'relative max-w-[80%] rounded-lg px-3 py-2 shadow-sm',
            isAssistant
              ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100'
              : 'bg-primary-100 dark:bg-primary-800 text-gray-900 dark:text-gray-100'
          )}
        >
          {/* Message content */}
          {isLoading ? (
            <div className="flex items-center space-x-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Processando resposta...</span>
            </div>
          ) : (
            <div
              className="text-sm break-words leading-relaxed"
              dangerouslySetInnerHTML={{ __html: formatWhatsAppText(content) }}
            />
          )}

          {/* Message timestamp */}
          <div className="text-xs mt-1 opacity-70 text-right">
            {timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>
      </div>
    );
  }
);

ChatMessage.displayName = 'ChatMessage';

export default ChatMessage;
